import { useCallback, useMemo, useState } from "react";

import {
    BsMetaUtil,
    StrategyExpandedInfo,
    useLoopscaleWalletContext,
    useBulkStrategyDepositTransaction,
    useBulkStrategyWithdrawTransaction,
    useDepositStrategyTransaction,
    useStrategies,
    useUserAvailableAssets,
    useWithdrawStrategyTransaction
} from "@bridgesplit/abf-react";
import {
    BulkStrategyDepositParams,
    BulkStrategyWithdrawParams,
    DepositStrategyTxnParams,
    WithdrawStrategyTxnParams
} from "@bridgesplit/abf-sdk";
import { MISSING_PARAM_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import { useTransactionSender } from "app/components/transactions";
import { TrackTransactionEvent } from "app/types";

export function useStrategyDeposit() {
    const send = useTransactionSender();

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const deposit = useDepositStrategyTransaction();

    return async ({ amount, strategy }: { amount: number | undefined; strategy: StrategyExpandedInfo | undefined }) => {
        if (!strategy || !amount || !activeWallet) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const description = `Depositing ${BsMetaUtil.formatAmount(strategy.principalMetadata, amount)}`;

        const depositParams: DepositStrategyTxnParams = {
            strategy: strategy.strategy.address,
            amount: uiAmountToLamports(amount, strategy.principalMetadata.decimals)
        };
        return await send(deposit, depositParams, {
            description,
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitStrategyDeposit,
                params: {
                    strategyAddress: strategy.strategy.address,
                    amount: depositParams.amount,
                    uiAmount: amount
                }
            }
        });
    };
}
export interface BulkStrategyDepositEntry {
    strategy: StrategyExpandedInfo;
    amount: number | undefined;
}

export function useBulkStrategyDeposit() {
    const send = useTransactionSender();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const deposit = useBulkStrategyDepositTransaction();

    return async ({ entries }: { entries: BulkStrategyDepositEntry[] }) => {
        if (!activeWallet) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const validEntries: BulkStrategyDepositParams[] = entries
            .filter((e): e is { strategy: StrategyExpandedInfo; amount: number } => !!e.amount && e.amount > 0)
            .map(({ strategy, amount }) => ({
                strategy: strategy.strategy.address,
                amount: uiAmountToLamports(amount, strategy.principalMetadata.decimals)
            }));

        if (validEntries.length === 0) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const description =
            validEntries.length > 1 ? `Depositing across ${validEntries.length} strategies` : "Depositing";

        return await send(
            deposit,
            { entries: validEntries },
            {
                description,
                mixpanelEvent: {
                    key: TrackTransactionEvent.SubmitBulkStrategyDeposit,
                    params: {
                        count: validEntries.length,
                        entries: validEntries
                    }
                }
            }
        );
    };
}

export interface BulkStrategyWithdrawEntry {
    strategy: StrategyExpandedInfo;
    amount: number | undefined;
    withdrawAll: boolean;
}

export function useBulkStrategyWithdraw() {
    const send = useTransactionSender();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const withdraw = useBulkStrategyWithdrawTransaction();

    return async ({ entries }: { entries: BulkStrategyWithdrawEntry[] }) => {
        if (!activeWallet) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const validEntries: BulkStrategyWithdrawParams[] = entries
            .filter((e): e is { strategy: StrategyExpandedInfo; amount: number; withdrawAll: boolean } => {
                if (e.withdrawAll) return true;
                return !!e.amount && e.amount > 0;
            })
            .map(({ strategy, amount, withdrawAll }) => ({
                strategy: strategy.strategy.address,
                amount: uiAmountToLamports(amount ?? 0, strategy.principalMetadata.decimals),
                withdrawAll
            }));

        if (validEntries.length === 0) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const description =
            validEntries.length > 1 ? `Withdrawing from ${validEntries.length} strategies` : "Withdrawing";

        return await send(
            withdraw,
            { entries: validEntries },
            {
                description,
                mixpanelEvent: {
                    key: TrackTransactionEvent.SubmitBulkStrategyWithdraw,
                    params: {
                        count: validEntries.length,
                        entries: validEntries
                    }
                }
            }
        );
    };
}

export function useBulkStrategyAmounts(strategyAddresses: string[] | undefined) {
    const { data: strategies, isLoading } = useStrategies(
        { addresses: strategyAddresses, pageSize: Math.max(strategyAddresses?.length ?? 0, 1) },
        !strategyAddresses?.length
    );

    const [amounts, setAmounts] = useState<Map<string, number | undefined>>(new Map());

    const setAmount = useCallback((address: string, value: number | undefined) => {
        setAmounts((prev) => {
            const next = new Map(prev);
            next.set(address, value);
            return next;
        });
    }, []);

    const clearAmounts = useCallback(() => setAmounts(new Map()), []);

    const strategiesByAddress = useMemo(() => {
        const m = new Map<string, StrategyExpandedInfo>();
        strategies?.forEach((s) => m.set(s.strategy.address, s));
        return m;
    }, [strategies]);

    const nonZeroCount =
        strategyAddresses?.reduce((acc, address) => {
            const amt = amounts.get(address);
            return amt && amt > 0 ? acc + 1 : acc;
        }, 0) ?? 0;

    return {
        strategies,
        isLoading,
        strategiesByAddress,
        amounts,
        setAmount,
        clearAmounts,
        nonZeroCount,
        hasAnyAmount: nonZeroCount > 0
    };
}

export interface BulkStrategyDepositUniqueMint {
    mint: string;
    metadata: StrategyExpandedInfo["principalMetadata"];
    walletBalance: number;
    totalEntered: number;
    isInsufficient: boolean;
}

export interface BulkStrategyDepositBalances {
    uniqueMints: BulkStrategyDepositUniqueMint[];
    getRemainingForAddress: (address: string) => number | undefined;
    anyInsufficient: boolean;
    isLoading: boolean;
}

export function useBulkStrategyDepositBalances(
    strategies: StrategyExpandedInfo[] | undefined,
    amounts: Map<string, number | undefined>
): BulkStrategyDepositBalances {
    const { data, isLoading } = useUserAvailableAssets({});

    const walletBalanceByMint = useMemo(() => {
        const m = new Map<string, number>();
        data?.forEach((d) => m.set(d.metadata.mint, d.amount));
        return m;
    }, [data]);

    const totalByMint = useMemo(() => {
        const m = new Map<string, number>();
        strategies?.forEach((s) => {
            const mint = s.principalMetadata?.mint;
            if (!mint) return;
            const amt = amounts.get(s.strategy.address) ?? 0;
            m.set(mint, (m.get(mint) ?? 0) + amt);
        });
        return m;
    }, [strategies, amounts]);

    const uniqueMints = useMemo<BulkStrategyDepositUniqueMint[]>(() => {
        if (!strategies) return [];
        const seen = new Map<string, StrategyExpandedInfo["principalMetadata"]>();
        strategies.forEach((s) => {
            const mint = s.principalMetadata?.mint;
            if (!mint || seen.has(mint)) return;
            seen.set(mint, s.principalMetadata);
        });
        return Array.from(seen.entries()).map(([mint, metadata]) => {
            const walletBalance = walletBalanceByMint.get(mint) ?? 0;
            const totalEntered = totalByMint.get(mint) ?? 0;
            return {
                mint,
                metadata,
                walletBalance,
                totalEntered,
                isInsufficient: totalEntered > walletBalance
            };
        });
    }, [strategies, walletBalanceByMint, totalByMint]);

    const mintByAddress = useMemo(() => {
        const m = new Map<string, string>();
        strategies?.forEach((s) => {
            const mint = s.principalMetadata?.mint;
            if (mint) m.set(s.strategy.address, mint);
        });
        return m;
    }, [strategies]);

    const getRemainingForAddress = useCallback(
        (address: string): number | undefined => {
            if (isLoading) return undefined;
            const mint = mintByAddress.get(address);
            if (!mint) return undefined;
            const walletBalance = walletBalanceByMint.get(mint) ?? 0;
            const totalEntered = totalByMint.get(mint) ?? 0;
            const own = amounts.get(address) ?? 0;
            return walletBalance - (totalEntered - own);
        },
        [isLoading, mintByAddress, walletBalanceByMint, totalByMint, amounts]
    );

    const anyInsufficient = uniqueMints.some((m) => m.isInsufficient);

    return { uniqueMints, getRemainingForAddress, anyInsufficient, isLoading };
}

export function useStrategyWithdraw() {
    const send = useTransactionSender();

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const withdraw = useWithdrawStrategyTransaction();

    return async ({
        amount,
        strategy,
        withdrawAll
    }: {
        amount: number | undefined;
        strategy: StrategyExpandedInfo | undefined;
        withdrawAll: boolean;
    }) => {
        if (!strategy || !amount || !activeWallet) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const description = `Withdrawing ${BsMetaUtil.formatAmount(strategy.principalMetadata, amount)}`;

        const withdrawParams: WithdrawStrategyTxnParams = {
            strategy: strategy.strategy.address,
            amount: uiAmountToLamports(amount, strategy.principalMetadata.decimals),
            withdrawAll
        };
        return await send(withdraw, withdrawParams, {
            description,
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitStrategyWithdraw,
                params: {
                    strategyAddress: strategy.strategy.address,
                    amount: withdrawParams.amount,
                    uiAmount: amount,
                    withdrawAll
                }
            }
        });
    };
}
